import { merge } from 'lodash-es';
import { getClientLocalTimeZone, nativeDateToLiveEventTime } from '../../helpers/event-time';
import {
  LiveEvent,
  LiveBlogModalView,
  LiveBlogModalMode,
  LiveEventTime
} from './../../state/types';
import User from 'app/scripts/models/user';

export type LiveBlogModalReducer = typeof liveBlogModalReducer;
export interface ILiveBlogModalState {
  view: LiveBlogModalView;
  mode: LiveBlogModalMode;
  startTime: LiveEventTime;
  endTime: LiveEventTime;
  displayName: string;
  timezone: string;
  reporterIds: string[];
  timeError: TTimeError;
  isLoading: boolean;
  eventCode: string;
  eventId: string;
  reportersError: boolean;
}

interface TTimeError {
  error: boolean;
  field?: 'start' | 'end' | 'none';
  reason?: 'start' | 'both' | 'none';
}

export enum TAction {
  ViewChange = 'view_change',
  EventDetailsChange = 'event_details_change',
  AddReporter = 'add_reporter',
  RemoveReporter = 'remove_reporter',
  CreateOrUpdateEvent = 'create_or_update_event',
  CreateOrUpdateEventSuccess = 'create_or_update_success'
}

export type LiveBlogModalAction =
  | { type: TAction.ViewChange; payload: { view: LiveBlogModalView } }
  | {
      type: TAction.EventDetailsChange;
      payload: { displayName?: string; timezone?: string; startTime?: Date; endTime?: Date };
    }
  | { type: TAction.AddReporter; payload: { userId: string; user?: User } }
  | { type: TAction.RemoveReporter; payload: { userId: string; user?: User } }
  | { type: TAction.CreateOrUpdateEvent }
  | {
      type: TAction.CreateOrUpdateEventSuccess;
      payload: { liveEvent?: Partial<LiveEvent>; status: 'success' | 'failure' };
    };

export const liveBlogModalReducer = (
  state: ILiveBlogModalState,
  action: LiveBlogModalAction
): ILiveBlogModalState => {
  switch (action.type) {
    case TAction.ViewChange:
      const { view } = action.payload;
      return { ...state, view };
    case TAction.EventDetailsChange:
      const { displayName, timezone, startTime, endTime } = action.payload;

      let start, end, timeError;

      if (startTime || endTime) {
        if (startTime) {
          start = nativeDateToLiveEventTime(startTime);
        }

        if (endTime) {
          end = nativeDateToLiveEventTime(endTime);
        }

        const error =
          (startTime?.getTime() || state.startTime?.time * 1000) >=
          (endTime?.getTime() || state.endTime?.time * 1000);
        const errorField = error ? (startTime ? 'start' : endTime ? 'end' : 'none') : 'none';

        timeError = {
          error,
          field: errorField
        };
      }

      return merge({}, state, {
        displayName,
        timezone,
        startTime: start,
        endTime: end,
        timeError
      });
    case TAction.AddReporter:
      const { userId: toAdd } = action.payload;

      return { ...state, reporterIds: [toAdd, ...state.reporterIds], reportersError: false };
    case TAction.RemoveReporter:
      const { userId: toRemove } = action.payload;
      const newReportersList = state.reporterIds.filter(reporterId => reporterId !== toRemove);

      return {
        ...state,
        reporterIds: newReportersList,
        reportersError: newReportersList.length === 0
      };
    case TAction.CreateOrUpdateEvent:
      return { ...state, isLoading: true };
    case TAction.CreateOrUpdateEventSuccess:
      const { liveEvent, status } = action.payload;
      return { ...state, isLoading: false, eventCode: liveEvent.eventCode, eventId: liveEvent.id };
    default:
      return state;
  }
};

// This function will be served as the third argument for the reducer,
// based on mode, view and event we will generate the initial reducer's state.
export const initState = ({
  view = LiveBlogModalView.EventDetails,
  mode = LiveBlogModalMode.Create,
  event
}: {
  event: Partial<LiveEvent>;
  view: LiveBlogModalView;
  mode: LiveBlogModalMode;
}): ILiveBlogModalState => {
  const isEditMode = mode === LiveBlogModalMode.Edit;
  const displayName = event.title
    ? `${mode === LiveBlogModalMode.Duplicate ? 'Copy of ' : ''}${event.title}`
    : '';
  return {
    view,
    mode,
    displayName,
    eventId: event.id ?? null,
    startTime: isEditMode ? event?.start : null,
    endTime: isEditMode ? event?.end : null,
    timezone: event?.timeZone || getClientLocalTimeZone(),
    reporterIds: event?.reporterIds ?? [],
    isLoading: false,
    eventCode: event?.eventCode,
    timeError: {
      error: false,
      field: 'none',
      reason: 'none'
    },
    reportersError: false
  };
};

export const isFormValid = (
  state: ILiveBlogModalState,
  { mode, view }: { mode: LiveBlogModalMode; view: LiveBlogModalView }
) => {
  switch (view) {
    case LiveBlogModalView.EventDetails:
      const isEditMode = mode === LiveBlogModalMode.Edit;
      const { startTime, endTime, timeError, displayName } = state;
      const timesSet = !timeError.error && startTime && endTime && startTime.time && endTime.time;
      const isValidTimes = timesSet && startTime.time < endTime.time;
      const isValidDisplayName = displayName?.length > 0;
      return isEditMode ? true : isValidTimes && isValidDisplayName;
    case LiveBlogModalView.Team:
      return state.reporterIds.length > 0;
    case LiveBlogModalView.EmbedCode:
    default:
      return true;
  }
};
